import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';
import { Observable, Subscriber } from 'rxjs';
import { Router } from '@angular/router';
import { User } from '../model/user';
import { UserPermissionResponse } from '../model/response/user-permission-response';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LoggerFactory } from '@elderbyte/ts-logger';
import { Select, Store } from '@ngxs/store';
import { AuthLogout, AuthRefresh } from '../../state/auth/auth.actions';
import { AuthState } from '../../state/auth/auth.state';

@Injectable()
export class UserService {

  private static logger = LoggerFactory.getLogger('UserService');

  @Select(state => state.user) user$: Observable<any>;


  constructor(private store: Store,
              private authService: AuthService,
              private tokenService: TokenService,
              private router: Router,
              private http: HttpClient) {
    // Get token after page refresh
    if (this.tokenService.getToken()) {
      this.refreshAuth(this.tokenService.getToken());
    }
  }


  /**
   * Refresh authorization
   * @param {string} token The refreshed token
   */
  refreshAuth(token: string): Observable<void> {
    return this.store.dispatch(new AuthRefresh(token));
  }

  /**
   * Get a list of permissions the currently logged in user has
   * @returns {Observable<UserPermissionResponse>}
   */
  getPermissions(): Observable<UserPermissionResponse> {
    return new Observable<UserPermissionResponse>((subscriber: Subscriber<UserPermissionResponse>) => {
      this.http.get(environment.endpoints.clientBaseUrl + environment.endpoints.getPermissions)
        .subscribe(
          (response: UserPermissionResponse) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Change the current user's password
   * @param {string} currentPassword The current user's password
   * @param {string} newPassword The new password
   * @returns {Observable<User>}
   */
  changePassword(currentPassword: string, newPassword: string): Observable<User> {
    return new Observable<User>((subscriber: Subscriber<User>) => {
      this.authService.changePassword(currentPassword, newPassword)
        .subscribe(
          (user: User) => {
            subscriber.next(user);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Log the currently logged in user out
   */
  logout(errorState: boolean) {
    if (errorState) {
      this.router.navigate(['/login']);
    } else {
      this.store.dispatch(new AuthLogout()).subscribe(() => {
        this.router.navigate(['/login']);
      });
    }
  }


  /**
   * Checks if the current client is authenticated
   * @returns {boolean} Whether the current client is authenticated or not
   */
  isAuthenticated(): boolean {
    return !!this.store.selectSnapshot(AuthState.token) && !!this.store.selectSnapshot(AuthState.user);
  }

  /**
   * Get the username of the currently logged-in user
   * @returns The username of the currently logged-in user
   */
  getUserName(): string {
    return this.store.selectSnapshot(AuthState.user).sub;
  }

  /**
   * Build the value of the 'Authorization' header based on the current authorization token
   * @returns {string} The final header value
   */
  buildAuthHeader(): string {
    return `Bearer ${this.store.selectSnapshot(AuthState.token)}`;
  }

}
